/*
NOTE: This page is served by unified-google-certificates. This is because this page has DISABLED ALL AB TESTING.
This was a product requirement for the google-owned description pages. Making updates to this page is the same as any other page,
in this application (house all components in unified-description-page). It's just served by a different app. 
*/
import * as React from 'react';

import {
  DescriptionPage_SelfServeConfiguration_ModuleName as ModuleNames,
  DescriptionPage_ProductVariant as ProductVariant,
} from '__generated__/graphql-types';

import { useGenerateStartFreeTrialTestEpicImpression } from 'bundles/coursera-plus-common/components/elevateLTV/startFreeTrial/useGenerateStartFreeTrialTestEpicImpression';
import useGenerateGooglePriceTestImpression from 'bundles/payments/utils/useGenerateGooglePriceTestImpression';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import AdminBannerNotConfigurable from 'bundles/unified-description-page-common/components/admin/AdminBannerNotConfigurable';
import ConsumerSpecializationEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/consumer/ConsumerSpecializationEnrollButtonNotConfigurable';
import MobileCtaModule from 'bundles/unified-description-page-common/components/modules/mobile-cta/MobileCtaModule';
import FixedPageNavigation from 'bundles/unified-description-page-common/components/modules/page-navigation/FixedPageNavigation';
import NavPartnerLogo from 'bundles/unified-description-page-common/components/modules/page-navigation/NavPartnerLogo';
import SpecializationAndProfessionalCertificatePageSEO from 'bundles/unified-description-page-common/components/seo/SpecializationAndProfessionalCertificatePageSEO';
import CoBrandingPartnerLogos from 'bundles/unified-description-page-common/components/shared/CoBrandingPartnerLogos';
import { useSpecializationPageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import useLogRecentlyViewed from 'bundles/unified-description-page-common/hooks/useRecentlyViewed';
import ConsumerProfessionalCertificateCitationModuleRenderer from 'bundles/unified-description-page-common/module-renderers/citation/ConsumerProfessionalCertificateCitationModuleRenderer';
import Renderer from 'bundles/unified-description-page-common/pages/consumer/google-certificate/ConsumerGoogleCertificatePageRenderer';
import EventingDataProvider from 'bundles/unified-description-page-common/providers/EventingDataProvider';
import LanguageInfoDialogProvider from 'bundles/unified-description-page-common/providers/LanguageInfoDialogProvider';
import PageInfoProvider from 'bundles/unified-description-page-common/providers/PageInfoProvider';
import PageNavigationProvider from 'bundles/unified-description-page-common/providers/PageNavigationProvider';
import StickyPanelProvider from 'bundles/unified-description-page-common/providers/StickyPanelProvider';
import TranslationsNotificationBannerProvider from 'bundles/unified-description-page-common/providers/consumer/TranslationsNotificationBannerProvider';
import {
  SPECIALIZATION_NAV_ITEMS,
  getSpecializationPageNavigationLinks,
} from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import getPartnersForSpecialization from 'bundles/unified-description-page-common/utils/getPartnersForSpecialization';
import getProductId from 'bundles/unified-description-page-common/utils/getProductId';
import getVariantTitle from 'bundles/unified-description-page-common/utils/getVariantTitle';
import hasCareerOutcomesModule from 'bundles/unified-description-page-common/utils/hasCareerOutcomesModule';
import useIsSimplifiedGoogleXdp from 'bundles/unified-description-page-common/utils/useIsSimplifiedGoogleXdp';

const MODULES_TO_REMOVE = [
  ModuleNames.CareerOutcomes,
  ModuleNames.Testimonials,
  ModuleNames.ArticlesRecommendations,
  ModuleNames.Faqs,
];

const ConsumerProfessionalCertificateConfigurablePageRootContent = () => {
  // Prof certs use same data as for specializations.
  let pageData = useSpecializationPageData();
  const isSimplifiedGoogleXdp = useIsSimplifiedGoogleXdp();

  useLogRecentlyViewed({
    name: pageData?.name,
    slug: pageData?.slug,
    partners: pageData?.partners,
    id: getProductId('specialization', pageData?.id),
    difficulty: pageData?.difficultyLevel,
    productType: pageData?.productVariant,
  });

  useGenerateGooglePriceTestImpression({
    hasSpecialization: true,
    partners: pageData?.partners,
  });

  useGenerateStartFreeTrialTestEpicImpression({});

  if (!pageData) {
    return null;
  }

  if (isSimplifiedGoogleXdp) {
    pageData = {
      ...pageData,
      ...(pageData?.selfServeConfiguration && {
        selfServeConfiguration: {
          ...pageData.selfServeConfiguration,
          modules: pageData.selfServeConfiguration.modules?.filter(
            (module) => !MODULES_TO_REMOVE.includes(module.name)
          ),
        },
      }),
    };
  }

  const partnersToDisplay = getPartnersForSpecialization(pageData.partners, !!pageData.coBrandingEnabled);
  const productTitle = getProductTitle(pageData.name, ProductVariant.GoogleCertificate);

  const navigationLinks = getSpecializationPageNavigationLinks({
    pageData,
  });

  return (
    <PageInfoProvider navigationIdMap={SPECIALIZATION_NAV_ITEMS} productSlug={pageData.slug}>
      <EventingDataProvider productId={pageData.id} productType="s12n">
        <PageNavigationProvider navigationLinks={navigationLinks}>
          <StickyPanelProvider>
            <LanguageInfoDialogProvider
              languages={{
                primaryLanguages: pageData.primaryLanguages,
                subtitleLanguages: pageData.subtitleLanguages ?? [],
                translatedLanguages: pageData.translatedLanguages ?? [],
              }}
            >
              <TranslationsNotificationBannerProvider
                translatedLanguages={pageData.translatedLanguages}
                productType={getVariantTitle(pageData.productVariant)}
                originalLanguage={pageData.primaryLanguages[0]}
              >
                <AdminBannerNotConfigurable slug={pageData.slug} />
                <FixedPageNavigation
                  productTitle={productTitle}
                  navigationLinks={navigationLinks}
                  pageCta={
                    <ConsumerSpecializationEnrollButtonNotConfigurable
                      pageData={pageData}
                      size="small"
                      onlyShowButton
                    />
                  }
                  partnerLogo={
                    partnersToDisplay && partnersToDisplay.length > 1 ? (
                      <CoBrandingPartnerLogos partners={partnersToDisplay} isNavPlacement={true} />
                    ) : (
                      <NavPartnerLogo
                        // @ts-expect-error URL: string
                        imageSrc={partnersToDisplay?.[0].squareLogo}
                        partnerName={partnersToDisplay?.[0].name}
                      />
                    )
                  }
                />
                <Renderer pageData={pageData} pageConfig={pageData.selfServeConfiguration} />
                {hasCareerOutcomesModule(pageData.selfServeConfiguration) && (
                  <ConsumerProfessionalCertificateCitationModuleRenderer pageData={pageData} />
                )}
                <MobileCtaModule
                  pageCta={
                    <ConsumerSpecializationEnrollButtonNotConfigurable
                      pageData={pageData}
                      size="small"
                      onlyShowButton
                    />
                  }
                />
                <SpecializationAndProfessionalCertificatePageSEO pageData={pageData} disableCrawlerIndexing />
              </TranslationsNotificationBannerProvider>
            </LanguageInfoDialogProvider>
          </StickyPanelProvider>
        </PageNavigationProvider>
      </EventingDataProvider>
    </PageInfoProvider>
  );
};

export default ConsumerProfessionalCertificateConfigurablePageRootContent;
